import { useMemo, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Slider,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';

interface JsonSchemaField {
  type?: string;
  enum?: string[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  title?: string;
}

interface ParametersSchema {
  type?: string;
  required?: string[];
  properties?: Record<string, JsonSchemaField>;
}

interface AIActionFormProps {
  schema: unknown;
  onSubmit: (params: Record<string, unknown>) => Promise<void> | void;
  onCancel: () => void;
  initialValues?: Record<string, unknown>;
}

/**
 * Hand-rolled renderer for a JSON-Schema object with the subset the registry uses:
 * string / number / integer / boolean / enum. Good enough for Phase 1 — an rjsf-based
 * renderer can replace this when @rjsf/mui catches up to MUI v7.
 */
export function AIActionForm({
  schema,
  onSubmit,
  onCancel,
  initialValues,
}: AIActionFormProps) {
  const parsed = schema as ParametersSchema | undefined;
  const properties = parsed?.properties ?? {};
  const required = new Set(parsed?.required ?? []);

  const defaults = useMemo(() => {
    const result: Record<string, unknown> = { ...initialValues };
    for (const [name, field] of Object.entries(properties)) {
      if (result[name] === undefined && field.default !== undefined) {
        result[name] = field.default;
      }
    }
    return result;
  }, [properties, initialValues]);

  const [values, setValues] = useState<Record<string, unknown>>(defaults);
  const [submitting, setSubmitting] = useState(false);

  const set = (name: string, value: unknown) =>
    setValues((v) => ({ ...v, [name]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Object.entries(properties).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              This action takes no parameters.
            </Typography>
          )}
          {Object.entries(properties).map(([name, field]) => (
            <FieldRenderer
              key={name}
              name={name}
              field={field}
              value={values[name]}
              required={required.has(name)}
              onChange={(v) => set(name, v)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Running…' : 'Run'}
        </Button>
      </DialogActions>
    </>
  );
}

function FieldRenderer({
  name,
  field,
  value,
  required,
  onChange,
}: {
  name: string;
  field: JsonSchemaField;
  value: unknown;
  required: boolean;
  onChange: (value: unknown) => void;
}) {
  const title = field.title ?? name;

  if (field.enum) {
    return (
      <FilterableSelect
        options={field.enum.map((opt) => ({ value: opt, label: opt }))}
        value={(value as string) ?? ''}
        onChange={(newValue) => onChange(newValue)}
        label={required ? `${title} *` : title}
        allowEmpty={false}
      />
    );
  }

  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={title}
      />
    );
  }

  if (field.type === 'integer' || field.type === 'number') {
    if (field.minimum !== undefined && field.maximum !== undefined) {
      return (
        <Stack spacing={1}>
          <Typography variant="caption">
            {title}: {String(value ?? field.default ?? field.minimum)}
          </Typography>
          <Slider
            min={field.minimum}
            max={field.maximum}
            step={field.type === 'integer' ? 1 : 0.05}
            value={
              (value as number) ?? (field.default as number) ?? field.minimum
            }
            onChange={(_, v) => onChange(v)}
          />
        </Stack>
      );
    }
    return (
      <TextField
        type="number"
        label={required ? `${title} *` : title}
        value={value ?? ''}
        onChange={(e) =>
          onChange(
            field.type === 'integer'
              ? parseInt(e.target.value, 10)
              : parseFloat(e.target.value),
          )
        }
      />
    );
  }

  // Default: text
  return (
    <TextField
      label={required ? `${title} *` : title}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      multiline={(field.maxLength ?? 0) > 200}
      minRows={(field.maxLength ?? 0) > 200 ? 3 : 1}
    />
  );
}
